
import moment from 'moment'
import Api from '@/Plugins/api'
import Version from '@/version.json'

const state = () => ({
  /**
   * The order's ID.
   * @type Number
   */
  id: undefined,

  /**
   * The invoice's number.
   * @type String
   */
  number: undefined,

  /**
   * The invoice's signature.
   * @type String
   */
  signature: undefined,
})

const getters = {
  business: (state, getters, rootState, rootGetters) => prop => {
    const data = rootGetters['fiscalCountry/config'](prop)
    if (prop === 'nif') {
      return `SIRET: ${data}`
    }
    if (prop === 'city') {
      const country = rootGetters['fiscalCountry/config']('country')
      return `${data} - ${country}`
    }
    return data
  },
  ticketInfo: (state, getters, rootState, rootGetters) => [
    `Opérateur: ${rootState.orders.current.tenantUserName} (${rootState.orders.current.tenantUser_id})`,
    'Vendeur: ' + rootGetters['user/fullName'] + ' (' + (rootState.user.id ?? '?') + ')',
  ],
  invoiceInfo: state => null,
  fiscalInfo: state => null,
  fiscalData: state => null,
  orderMaxAmount: state => null,
  contentPrice: (state, getters) => ({ content, invoice }) => {
    const tax = !! invoice ? `${getters._tax(content.taxPercentage)} ` : ''
    return tax + Number(content.itemPrice || 0).toFixed(2)
  },
  // from APP's FrancePrintable.swift
  _tax: state => taxPercentage => {
    const tax = parseFloat(taxPercentage)
    if (tax < 5) {
      return 'D'
    }
    if (tax < 9) {
      return 'C'
    }
    if (tax < 15) {
      return 'B'
    }
    return 'A'
  },
  shouldAddReason: state => true,
  shouldSignInvoice: state => true,
}

const mutations = {
  id (state, value) {
    state.id = value
  },
  number (state, value) {
    state.number = value
  },
  signature (state, value) {
    state.signature = value
  },
}

const actions = {
  printPreHeader ({}, { order, invoice }) {},
  printAdditionalHeader ({ state, rootGetters }, business) {
    AndroidPrinter.text2(
      'TVA: ' + rootGetters['fiscalCountry/config']('TVA'),
      'NAF: ' + rootGetters['fiscalCountry/config']('NAF')
    )
    AndroidPrinter.text2(
      `Ticket de caisse: ${state.number}`,
      `Note: ${state.id}`
    )
    const now = moment().format('YYYY-MM-DD HH:mm:ss')
    AndroidPrinter.text(`Date: ${now}`, JSON.stringify({ align: 'left' }))
    const hash = `B-0234-${state.signature[2]}${state.signature[6]}${state.signature[12]}${state.signature[18]}`
    AndroidPrinter.text(`NF525 Hash: ${hash}`, JSON.stringify({ align: 'left' }))
  },
  printAdditionalFooter ({ rootGetters }) {
    const footer = [
      'Type Op: Vente',
      'Code Caisse: ' + rootGetters['rummage/cashier']?.id,
      'N. Lignes: ' + rootGetters['orders/lines'],
      'Version L. Revo HANDY: ' + Version.short,
    ]
    footer.forEach(line => {
      AndroidPrinter.text(line, JSON.stringify({ align: 'left' }))
    })
  },
  printCheckSummary ({ rootGetters }, order) {
    if (Number(order.discountAmount || 0).notEqualTo(0)) {
      AndroidPrinter.text2(
        rootGetters['translate']('discount'),
        Number(order.discountAmount || 0).toFixed(2)
      )
    }
    AndroidPrinter.text2(
      `${rootGetters['translate']('total')} (EUR)`,
      Number(order.total || 0).toFixed(2),
      JSON.stringify({ size: 'large' })
    )
  },
  printInvoiceSummary ({ getters, rootGetters }, { order, invoice }) {
    AndroidPrinter.text2(rootGetters['translate']('sum'), invoice.sum.toFixed(2))
    if (invoice.discountAmount.notEqualTo(0)) {
      AndroidPrinter.text2(rootGetters['translate']('discount'), invoice.discountAmount.toFixed(2))
    }
    AndroidPrinter.text2(rootGetters['translate']('subtotal'), invoice.subtotal.toFixed(2))
    invoice.appliedTaxes.forEach(appliedTax => AndroidPrinter.text3(
      `${getters._tax(appliedTax.tax_percentage)} ${parseFloat(appliedTax.tax_percentage).toFixed(1)}% (${appliedTax.base_amount.toFixed(2)})`,
      appliedTax.tax_amount.toFixed(2),
      (appliedTax.base_amount + appliedTax.tax_amount).toFixed(2)
    ))
    AndroidPrinter.text2(
      `${rootGetters['translate']('total')} (EUR)`,
      invoice.total.toFixed(2),
      JSON.stringify({ size: 'large' })
    )
  },
  async signPrintCheck ({ commit, dispatch, rootState }, { order, reason }) {
    try {
      const response = await Api(['printCheck', 'France'], {
        order_id: order.id,
        employee_id: order.tenantUser_id,
        cashier_id: rootState.user.data.cashier_id || rootState.settings.values.cashier,
        printContent: JSON.stringify(order),
        reason,
      })
      commit('id', response.data.order_id)
      commit('number', response.data.number)
      commit('signature', response.data.signature)
    } catch (e) {
      dispatch('$log', 'Failed to get fiscal country print check signature', { root: true })
      throw e
    }
  },
  async signInvoice ({ commit, dispatch, rootState }, invoice) {
    try {
      const response = await Api(['fiscalCountry', 'SignInvoice'], {
        id: invoice.id,
        cashier_id: rootState.user.data.cashier_id || rootState.settings.values.cashier,
      })
      commit('id', invoice.order_id)
      commit('number', response.data.number)
      commit('signature', response.data.signature)
    } catch (e) {
      dispatch('$log', 'Failed to get fiscal country invoice signature', { root: true })
      throw e
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
